import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { batchesService, marketTicksService } from "../../services/metalguard";
import { supabase } from "../../lib/supabase";
import { calcDynamicRiskBuffer, calcManualRiskBuffer, calculateDynamicBuffer } from "../../utils/riskBufferEngine";

import PrimaryNavBar from "components/ui/PrimaryNavBar";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";


import MetalInputForm from "./components/MetalInputForm";
import PriceBreakdown from "./components/PriceBreakdown";
import StressTestPanel from "./components/StressTestPanel";
import MarketSimulator from "./components/MarketSimulator";
import SaveBatchModal from "./components/SaveBatchModal";
import NegotiatedPriceSection from "./components/NegotiatedPriceSection";

const DEFAULT_FORM = {
  metalId: "",
  metal: "",
  metalBenchmarkType: "",
  metalBenchmarkSymbol: "",
  categoryId: "",
  category: "",
  weightGross: "",
  weightTare: "",
  yieldPct: 90,
  costLogistics: "",
  costProcessing: "",
  daysToSale: 30,
  targetMargin: 8,
  marginMode: 'PCT_OF_BENCHMARK',
  // Buffer de riesgo
  riskBufferMode: "dynamic", // 'dynamic' | 'manual'
  riskBufferManualPct: 3,    // solo usado cuando mode === 'manual'
  discountMode: "PCT_OF_BENCHMARK",
  discountValue: 5,
};

// ─── Benchmark resolution ────────────────────────────────────────────────────
async function resolveBenchmarkEUR(metal, userId) {
  if (!metal?.benchmark_symbol) return null;

  const { data: ticks } = await supabase?.from('market_ticks')?.select('*')?.eq('user_id', userId)?.eq('symbol', metal?.benchmark_symbol)?.order('ts', { ascending: false })?.limit(1);

  const tick = ticks?.[0] || null;
  if (!tick) return null;

  const tickValue = parseFloat(tick?.value);
  if (isNaN(tickValue) || tickValue <= 0) return null;

  // If the tick is already in EUR, return it directly (no FX conversion needed)
  if (tick?.currency === 'EUR') return tickValue;

  // Tick is in USD (LME USD-denominated) — convert to EUR
  if (metal?.benchmark_type === 'LME') {
    // Try EUR_USD first (canonical symbol in DB), then EURUSD as fallback
    const { data: fxTicks } = await supabase?.from('market_ticks')?.select('*')?.eq('user_id', userId)?.eq('symbol', 'EUR_USD')?.order('ts', { ascending: false })?.limit(1);
    let eurusd = parseFloat(fxTicks?.[0]?.value);
    if (!eurusd || isNaN(eurusd) || eurusd <= 0) {
      const { data: fxAlt } = await supabase?.from('market_ticks')?.select('*')?.eq('user_id', userId)?.eq('symbol', 'EURUSD')?.order('ts', { ascending: false })?.limit(1);
      eurusd = parseFloat(fxAlt?.[0]?.value);
    }
    if (!eurusd || isNaN(eurusd) || eurusd <= 0) eurusd = 1.08;
    return tickValue / eurusd;
  }

  return tickValue;
}

// ─── Fetch historical ticks for volatility calculation ───────────────────────
async function fetchHistoricalTicks(symbol, userId, days = 90) {
  if (!symbol || !userId) return [];
  const since = new Date();
  since?.setDate(since?.getDate() - days);
  const { data } = await supabase
    ?.from('market_ticks')
    ?.select('value, currency, ts')
    ?.eq('user_id', userId)
    ?.eq('symbol', symbol)
    ?.gte('ts', since?.toISOString())
    ?.order('ts', { ascending: true });
  return data || [];
}

// ─── Fetch EUR/USD rate ───────────────────────────────────────────────────────
async function fetchEurUsd(userId) {
  // Try EUR_USD first (canonical symbol in DB), then EURUSD as fallback
  const { data } = await supabase
    ?.from('market_ticks')
    ?.select('value')
    ?.eq('user_id', userId)
    ?.eq('symbol', 'EUR_USD')
    ?.order('ts', { ascending: false })
    ?.limit(1);
  const val = parseFloat(data?.[0]?.value);
  if (val && val > 0) return val;
  // Fallback to EURUSD symbol
  const { data: alt } = await supabase
    ?.from('market_ticks')
    ?.select('value')
    ?.eq('user_id', userId)
    ?.eq('symbol', 'EURUSD')
    ?.order('ts', { ascending: false })
    ?.limit(1);
  return parseFloat(alt?.[0]?.value) || 1.08;
}

// ─── Main price calculation ───────────────────────────────────────────────────
function calcMaxPrice(formData, benchmarkEUR, riskBufferResult) {
  // Do not silently fall back to 5000 — caller must ensure benchmark is valid
  const benchmark = benchmarkEUR;
  if (!benchmark || benchmark <= 0) return null;

  const grossKg = parseFloat(formData?.weightGross) || 0;
  const tareKg = parseFloat(formData?.weightTare) || 0;
  const netWeight = Math.max(0, grossKg - tareKg);
  const netTons = netWeight / 1000;
  const yieldFactor = formData?.yieldPct / 100;
  const yieldDeduction = benchmark * (1 - yieldFactor);
  const costLogistics = parseFloat(formData?.costLogistics) || 0;
  const costProcessing = parseFloat(formData?.costProcessing) || 0;
  const marginMode = formData?.marginMode ?? 'PCT_OF_BENCHMARK';
  const marginDeduction = marginMode === 'FIXED_EUR_PER_TON'
    ? (parseFloat(formData?.targetMargin) || 0)
    : benchmark * (formData?.targetMargin / 100);

  // Buffer de riesgo — viene del engine (dinámico o manual)
  const riskBufferDeduction = riskBufferResult?.riskBufferEurPerTon || 0;
  const riskBufferPct = riskBufferResult?.riskBufferPct || 0;

  // Discount is informational only — not deducted from max buy price base
  // (it is shown separately in NegotiatedPriceSection)
  let discountDeduction = 0;
  if (formData?.discountMode === 'PCT_OF_BENCHMARK') {
    discountDeduction = benchmark * ((formData?.discountValue || 0) / 100);
  } else {
    discountDeduction = parseFloat(formData?.discountValue) || 0;
  }

  const maxPurchasePrice = Math.max(0, benchmark - yieldDeduction - costLogistics - costProcessing - marginDeduction - riskBufferDeduction);
  const totalBatchPrice = maxPurchasePrice * netTons;

  // Risk score para badge visual
  const riskScore = riskBufferPct
    + (formData?.daysToSale > 60 ? 3 : 0)
    + (formData?.yieldPct < 85 ? 2 : 0);
  const riskLevel = riskScore >= 8 ? "alto" : riskScore >= 4 ? "medio" : "bajo";

  return {
    benchmarkValue: benchmark,
    yieldDeduction,
    yieldPct: formData?.yieldPct,
    costLogistics,
    costProcessing,
    marginDeduction,
    targetMargin: formData?.targetMargin,
    riskBufferDeduction,
    riskBufferPct,
    riskBufferMode: formData?.riskBufferMode,
    riskBufferDetails: riskBufferResult,
    discountDeduction,
    maxPurchasePrice,
    netWeight,
    totalBatchPrice,
    riskLevel,
  };
}

function calcStressResults(result, formData, benchmarkEUR, riskBufferResult) {
  const benchmark = benchmarkEUR || result?.benchmarkValue;
  if (!benchmark || benchmark <= 0) return null;
  const changes = { base: 0, down5: -5, down10: -10, down15: -15, up5: 5, up10: 10 };
  const out = {};
  Object.entries(changes)?.forEach(([key, pct]) => {
    const newBenchmark = benchmark * (1 + pct / 100);
    const yieldFactor = formData?.yieldPct / 100;
    const yieldDed = newBenchmark * (1 - yieldFactor);
    const marginDed = newBenchmark * (formData?.targetMargin / 100);
    // Recalcular buffer con nuevo benchmark
    const newRiskDed = riskBufferResult?.isManual
      ? (riskBufferResult?.riskBufferPct / 100) * newBenchmark * yieldFactor
      : (riskBufferResult?.riskBufferPct / 100) * newBenchmark * yieldFactor;
    const costLog = parseFloat(formData?.costLogistics) || 0;
    const costProc = parseFloat(formData?.costProcessing) || 0;
    out[key] = Math.max(0, newBenchmark - yieldDed - costLog - costProc - marginDed - newRiskDed);
  });
  return out;
}

function validate(formData, needsManualBenchmark, manualBenchmark) {
  const errs = {};
  if (!formData?.metalId) errs.metal = 'Selecciona un metal para continuar';
  if (!formData?.categoryId) errs.category = 'Selecciona una categoría';

  const gross = parseFloat(formData?.weightGross);
  if (!formData?.weightGross || formData?.weightGross === '') {
    errs.weightGross = 'El peso debe ser mayor que 0';
  } else if (isNaN(gross) || gross <= 0) {
    errs.weightGross = 'El peso debe ser mayor que 0';
  } else if (gross < 1) {
    errs.weightGross = 'El peso mínimo es 1 kg (0.001 t)';
  } else if (gross > 10000000) {
    errs.weightGross = 'Peso máximo 10.000 t (10.000.000 kg)';
  }

  if (formData?.weightTare) {
    const tare = parseFloat(formData?.weightTare);
    if (isNaN(tare) || tare < 0) errs.weightTare = 'Debe ser un valor positivo';
    else if (!isNaN(gross) && tare >= gross) errs.weightTare = 'La tara no puede ser mayor o igual al peso bruto';
  }

  const yieldVal = parseFloat(formData?.yieldPct);
  if (isNaN(yieldVal) || yieldVal <= 0) {
    errs.yieldPct = 'El yield debe ser mayor que 0%';
  } else if (yieldVal > 100) {
    errs.yieldPct = 'El yield no puede superar el 100%';
  }

  if (formData?.costLogistics !== '' && formData?.costLogistics !== undefined) {
    const cl = parseFloat(formData?.costLogistics);
    if (isNaN(cl) || cl < 0) errs.costLogistics = 'El valor no puede ser negativo';
  }

  if (formData?.costProcessing !== '' && formData?.costProcessing !== undefined) {
    const cp = parseFloat(formData?.costProcessing);
    if (isNaN(cp) || cp < 0) errs.costProcessing = 'El valor no puede ser negativo';
  }

  const dts = parseFloat(formData?.daysToSale);
  if (isNaN(dts) || dts < 1) {
    errs.daysToSale = 'El plazo debe ser al menos 1 día';
  } else if (dts > 365) {
    errs.daysToSale = 'El plazo máximo es 365 días';
  }

  const margin = parseFloat(formData?.targetMargin);
  if (!isNaN(margin) && margin < 0) {
    errs.targetMargin = 'El valor no puede ser negativo';
  }

  if (needsManualBenchmark) {
    const mb = parseFloat(manualBenchmark);
    if (!manualBenchmark || isNaN(mb) || mb <= 0) {
      errs.manualBenchmark = 'Introduce un precio de referencia válido';
    }
  }

  return errs;
}

export default function BatchCalculator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [stressResults, setStressResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isStressRunning, setIsStressRunning] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [benchmarkEUR, setBenchmarkEUR] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [manualBenchmark, setManualBenchmark] = useState("");
  const [latestTicks, setLatestTicks] = useState([]);
  const [noBenchmarkWarning, setNoBenchmarkWarning] = useState(false);

  // Historical ticks for volatility
  const [historicalTicks, setHistoricalTicks] = useState([]);
  const [eurUsd, setEurUsd] = useState(1.08);
  const [riskBufferResult, setRiskBufferResult] = useState(null);

  const needsManualBenchmark = formData?.metalId && !benchmarkLoading && benchmarkEUR === null;

  // Load latest market ticks for the header bar
  useEffect(() => {
    if (!user?.id) return;
    marketTicksService?.getLatest()?.then(({ data }) => setLatestTicks(data || []));
  }, [user?.id]);

  // Resolve benchmark + historical ticks when metal changes
  useEffect(() => {
    if (!formData?.metalId || !user?.id) {
      setBenchmarkEUR(null);
      setHistoricalTicks([]);
      setNoBenchmarkWarning(false);
      return;
    }
    setBenchmarkLoading(true);
    const metalObj = { benchmark_type: formData?.metalBenchmarkType, benchmark_symbol: formData?.metalBenchmarkSymbol };

    Promise.all([
      resolveBenchmarkEUR(metalObj, user?.id),
      fetchHistoricalTicks(formData?.metalBenchmarkSymbol, user?.id, 90),
      fetchEurUsd(user?.id),
    ])?.then(([benchVal, histTicks, fx]) => {
      setBenchmarkEUR(benchVal);
      setNoBenchmarkWarning(benchVal === null && !!formData?.metalBenchmarkSymbol);
      setHistoricalTicks(histTicks || []);
      setEurUsd(fx);
      setBenchmarkLoading(false);
    });
  }, [formData?.metalId, formData?.metalBenchmarkSymbol, formData?.metalBenchmarkType, user?.id]);

  // Recalculate risk buffer whenever relevant inputs change
  useEffect(() => {
    const effectiveBenchmark = benchmarkEUR || (manualBenchmark ? parseFloat(manualBenchmark) : null);
    if (!effectiveBenchmark) {
      setRiskBufferResult(null);
      return;
    }

    if (formData?.riskBufferMode === 'manual') {
      setRiskBufferResult(
        calcManualRiskBuffer(formData?.riskBufferManualPct, effectiveBenchmark, formData?.yieldPct)
      );
    } else {
      // Dynamic mode — use async engine that fetches historical EUR/USD internally
      if (formData?.metalBenchmarkSymbol && user?.id) {
        calculateDynamicBuffer({
          metalSymbol: formData?.metalBenchmarkSymbol,
          daysToSale: formData?.daysToSale,
          yieldPct: formData?.yieldPct,
          currentEurUsd: eurUsd,
          benchmarkEUR: effectiveBenchmark,
          userId: user?.id,
          benchmarkType: formData?.metalBenchmarkType,
        })?.then(result => setRiskBufferResult(result));
      } else {
        // Fallback to sync engine if no symbol/userId yet
        setRiskBufferResult(
          calcDynamicRiskBuffer({
            ticks: historicalTicks,
            eurUsd,
            benchmarkType: formData?.metalBenchmarkType,
            benchmarkEUR: effectiveBenchmark,
            daysToSale: formData?.daysToSale,
            yieldPct: formData?.yieldPct,
          })
        );
      }
    }
  }, [
    formData?.riskBufferMode,
    formData?.riskBufferManualPct,
    formData?.daysToSale,
    formData?.yieldPct,
    benchmarkEUR,
    manualBenchmark,
    historicalTicks,
    eurUsd,
    formData?.metalBenchmarkType,
    formData?.metalBenchmarkSymbol,
    user?.id,
  ]);

  const handleFormChange = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    // Clear only the errors for the fields being updated
    if (Object.keys(updates)?.length > 0) {
      setErrors((prev) => {
        const next = { ...prev };
        Object.keys(updates)?.forEach((k) => {
          // Map form field names to error keys
          if (k === 'weightGross') delete next?.weightGross;
          if (k === 'weightTare') delete next?.weightTare;
          if (k === 'yieldPct') delete next?.yieldPct;
          if (k === 'costLogistics') delete next?.costLogistics;
          if (k === 'costProcessing') delete next?.costProcessing;
          if (k === 'daysToSale') delete next?.daysToSale;
          if (k === 'targetMargin') delete next?.targetMargin;
          if (k === 'metalId') delete next?.metal;
          if (k === 'categoryId') delete next?.category;
        });
        return next;
      });
    }
    if (result) setResult(null);
    setStressResults(null);
  };

  // Derived: are all required fields valid enough to enable Calcular?
  const isFormReadyToCalculate = (() => {
    if (!formData?.metalId) return false;
    const gross = parseFloat(formData?.weightGross);
    if (!formData?.weightGross || isNaN(gross) || gross < 1) return false;
    const yieldVal = parseFloat(formData?.yieldPct);
    if (isNaN(yieldVal) || yieldVal <= 0 || yieldVal > 100) return false;
    return true;
  })();

  const handleCalculate = () => {
    const errs = validate(formData, needsManualBenchmark, manualBenchmark);
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      setTimeout(() => {
        const firstErr = document.querySelector('[data-field-error]');
        if (firstErr) {
          firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Try to focus the nearest input
          const parent = firstErr?.parentElement;
          const input = parent?.querySelector('input, select');
          if (input) input?.focus();
        }
      }, 50);
      return;
    }
    const effectiveBenchmark = benchmarkEUR || (manualBenchmark ? parseFloat(manualBenchmark) : null);
    if (!effectiveBenchmark || effectiveBenchmark <= 0) {
      setErrors({ manualBenchmark: 'No hay precio de referencia disponible. Introduce un precio manual.' });
      return;
    }
    setIsCalculating(true);
    const currentRiskBuffer = riskBufferResult || (
      formData?.riskBufferMode === 'manual'
        ? calcManualRiskBuffer(formData?.riskBufferManualPct, effectiveBenchmark, formData?.yieldPct)
        : calcDynamicRiskBuffer({
            ticks: historicalTicks,
            eurUsd,
            benchmarkType: formData?.metalBenchmarkType,
            benchmarkEUR: effectiveBenchmark,
            daysToSale: formData?.daysToSale,
            yieldPct: formData?.yieldPct,
          })
    );
    setTimeout(() => {
      const res = calcMaxPrice(formData, effectiveBenchmark, currentRiskBuffer);
      if (res) {
        setResult(res);
      } else {
        setErrors({ manualBenchmark: 'Error al calcular: precio de referencia no válido.' });
      }
      setIsCalculating(false);
    }, 600);
  };

  const handleReset = () => {
    setFormData(DEFAULT_FORM);
    setErrors({});
    setResult(null);
    setStressResults(null);
    setSavedSuccess(false);
    setBenchmarkEUR(null);
    setManualBenchmark("");
    setHistoricalTicks([]);
    setRiskBufferResult(null);
  };

  const handleRunStressTest = () => {
    if (!result) return;
    setIsStressRunning(true);
    setTimeout(() => {
      setStressResults(calcStressResults(result, formData, benchmarkEUR, riskBufferResult));
      setIsStressRunning(false);
    }, 700);
  };

  const handleSaveBatch = async ({ batchName, supplier, notes }) => {
    // The SaveBatchModal already called batchesService.create() internally.
    // This handler only needs to update UI state after a successful save.
    setSaveModalOpen(false);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 4000);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <div className="px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/main-dashboard')} className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-standard">
              <Icon name="ArrowLeft" size={18} color="var(--color-text-secondary)" strokeWidth={2} />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold" style={{ color: 'var(--color-text-primary)' }}>Calculadora de Lotes</h1>
              <p className="text-xs md:text-sm" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Calcule el precio máximo de compra para cada lote de metal</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {result && (
              <Button variant="outline" size="sm" iconName="Save" iconPosition="left" onClick={() => setSaveModalOpen(true)}>Guardar Lote</Button>
            )}
            <Button variant="ghost" size="sm" iconName="Layers" iconPosition="left" onClick={() => navigate('/active-batch-management')}>Ver Lotes</Button>
          </div>
        </div>
      </div>

      {savedSuccess && (
        <div className="fixed top-20 right-4 z-[300] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg spring-in" style={{ background: 'var(--color-success)', color: '#fff', maxWidth: '320px' }}>
          <Icon name="CheckCircle" size={18} color="#fff" strokeWidth={2.5} />
          <span className="text-sm font-medium" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Lote guardado correctamente</span>
        </div>
      )}

      <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-5 md:py-6">
        {/* Market ticker bar */}
        <div className="mb-5 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 border" style={{ background: 'var(--color-muted)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <div className="relative">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="absolute inset-0 inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping opacity-75" />
            </div>
            <span className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Mercado</span>
          </div>
          <div className="flex flex-wrap gap-4">
            {latestTicks?.filter(t => t?.symbol !== 'EURUSD')?.slice(0, 8)?.map((tick) => (
              <div key={tick?.id} className="flex items-center gap-1.5">
                <span className="text-xs font-semibold" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>{tick?.symbol?.slice(0, 6)}:</span>
                <span className="font-data text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>{parseFloat(tick?.value)?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} {tick?.currency}/t</span>
              </div>
            ))}
            {latestTicks?.length === 0 && (
              <span className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Sin datos de mercado — añada ticks en Datos de Mercado</span>
            )}
          </div>
          <span className="ml-auto text-xs font-data" style={{ color: 'var(--color-text-secondary)' }}>Actualizado: {new Date()?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 items-start">
          <div>
            <MetalInputForm
              formData={formData}
              errors={errors}
              onChange={handleFormChange}
              onCalculate={handleCalculate}
              onReset={handleReset}
              isCalculating={isCalculating}
              riskBufferResult={riskBufferResult}
              isFormReadyToCalculate={isFormReadyToCalculate}
              noBenchmarkWarning={noBenchmarkWarning}
            />
          </div>
          <div className="flex flex-col gap-5">
            <PriceBreakdown result={result} />
            <NegotiatedPriceSection result={result} formData={formData} />
            <StressTestPanel result={result} onRunStressTest={handleRunStressTest} isRunning={isStressRunning} stressResults={stressResults} />
            <MarketSimulator metal={formData?.metal} result={result} onSimulate={undefined} resolvedBenchmark={benchmarkEUR || (manualBenchmark ? parseFloat(manualBenchmark) : null)} />
            {result && (
              <div className="card p-4 flex flex-col sm:flex-row gap-3">
                <Button
                  variant="default"
                  fullWidth
                  iconName={isCalculating ? undefined : "Save"}
                  iconPosition="left"
                  loading={saving || isCalculating}
                  disabled={saving || isCalculating}
                  onClick={() => setSaveModalOpen(true)}
                >
                  {saving ? 'Guardando...' : isCalculating ? 'Calculando...' : 'Guardar Lote'}
                </Button>
                <Button variant="outline" fullWidth iconName="Layers" iconPosition="left" onClick={() => navigate('/active-batch-management')}>Gestión de Lotes</Button>
                <Button variant="ghost" fullWidth iconName="LayoutDashboard" iconPosition="left" onClick={() => navigate('/main-dashboard')}>Dashboard</Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <SaveBatchModal isOpen={saveModalOpen} onClose={() => setSaveModalOpen(false)} onSave={handleSaveBatch} result={result} formData={formData} />
    </div>
  );
}